// Settings > Vehicles: the models a brand services, each with one photo. Advisors
// pick from this list when booking, and the photo becomes the group's avatar.

import { useMemo, useRef, useState } from "react";
import { Loader2, Pencil, ImagePlus, X } from "lucide-react";
import { toast } from "sonner";
import {
  useCreateVehicle,
  useDeleteVehicle,
  useDismissUnmappedVehicle,
  useSuperadminVehicles,
  useUnmappedVehicles,
  useUpdateVehicle,
  useUploadVehicleImage,
} from "../../lib/api";
import { BODY_TYPES, bodyTypeLabel } from "../../lib/vehicles";
import { FieldError, FieldLabel, Select, Toggle, inputClass } from "./primitives";
import {
  Cell,
  ConfirmDelete,
  EmptyState,
  ErrorState,
  FormDialog,
  Loading,
  PageIntro,
  SearchBox,
  Table,
  Toolbar,
} from "./OrgKit";

const EMPTY = { name: "", body_type: "suv", active: true };

function Thumb({ url, name }) {
  return (
    <span className="flex h-11 w-16 items-center justify-center overflow-hidden rounded-lg bg-[#F3F4F6] shadow-[0_0_0_1px_#E5E7EB]">
      {url ? (
        <img src={url} alt={name} className="h-full w-full object-cover" />
      ) : (
        <span className="text-[10px] font-medium text-[#9CA3AF]">No photo</span>
      )}
    </span>
  );
}

function NeedsAdding({ tenantId, onAdd }) {
  const { data } = useUnmappedVehicles(tenantId);
  const dismiss = useDismissUnmappedVehicle(tenantId);
  const rows = data?.rows || [];
  if (!rows.length) return null;
  return (
    <div className="mt-6 rounded-2xl bg-[#FFFBEB] p-4 shadow-[0_0_0_1px_#FDE68A]" data-testid="vehicles-needs-adding">
      <div className="text-[13px] font-semibold text-[#92400E]">Advisors typed these under "Not listed"</div>
      <p className="mt-0.5 text-[12px] text-[#92400E]/80">Add the ones that are real models so they appear in the dropdown.</p>
      <div className="mt-3 flex flex-wrap gap-2">
        {rows.map((r) => (
          <span key={r.id} className="inline-flex items-center gap-1 rounded-full bg-white py-1 pl-3 pr-1 text-[12px] text-[#111827] shadow-[0_0_0_1px_#FDE68A]">
            <button type="button" onClick={() => onAdd(r)} className="font-medium hover:underline" title="Add to the catalog">
              {r.typed_name}
            </button>
            <span className="text-[#6B7280]">×{r.count}</span>
            <button
              type="button"
              aria-label={`Dismiss ${r.typed_name}`}
              onClick={() => dismiss.mutate(r.id)}
              className="flex h-6 w-6 items-center justify-center rounded-full text-[#9CA3AF] hover:bg-[#F3F4F6] hover:text-[#111827]"
            >
              <X size={12} />
            </button>
          </span>
        ))}
      </div>
    </div>
  );
}

export default function Vehicles({ tenantId }) {
  const { data, isLoading, isError, error, refetch } = useSuperadminVehicles(tenantId);
  const create = useCreateVehicle(tenantId);
  const update = useUpdateVehicle(tenantId);
  const remove = useDeleteVehicle(tenantId);
  const upload = useUploadVehicleImage(tenantId);
  const dismiss = useDismissUnmappedVehicle(tenantId);
  const fileRef = useRef(null);

  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const [fromUnmapped, setFromUnmapped] = useState(null);
  const [errors, setErrors] = useState({});
  const [deleting, setDeleting] = useState(null);

  const rows = useMemo(() => data?.rows || [], [data]);
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? rows.filter((r) => r.name.toLowerCase().includes(q)) : rows;
  }, [rows, query]);

  const preview = useMemo(() => (file ? URL.createObjectURL(file) : editing?.thumb_url || ""), [file, editing]);

  if (isLoading) return <Loading />;
  if (isError) return <ErrorState error={error} onRetry={refetch} />;

  const openAdd = (unmapped) => {
    setErrors({});
    setEditing(null);
    setFile(null);
    setFromUnmapped(unmapped?.id ? unmapped : null);
    setForm({ ...EMPTY, name: unmapped?.typed_name || "" });
    setOpen(true);
  };

  const openEdit = (row) => {
    setErrors({});
    setEditing(row);
    setFile(null);
    setFromUnmapped(null);
    setForm({ name: row.name, body_type: row.body_type, active: row.active });
    setOpen(true);
  };

  const submit = async () => {
    const name = form.name.trim();
    if (!name) return setErrors({ name: "Give the vehicle a name." });
    const clash = rows.some((r) => r.id !== editing?.id && r.name.trim().toLowerCase() === name.toLowerCase());
    if (clash) return setErrors({ name: "That vehicle is already in the catalog." });
    try {
      const saved = editing
        ? (await update.mutateAsync({ id: editing.id, ...form, name })).row
        : (await create.mutateAsync({ ...form, name })).row;
      if (file) await upload.mutateAsync({ id: saved.id, file });
      if (fromUnmapped) dismiss.mutate(fromUnmapped.id);
      toast.success(editing ? "Saved." : "Vehicle added.");
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save that.");
    }
  };

  const toggleActive = (row) =>
    update.mutate(
      { id: row.id, active: !row.active },
      { onError: (e) => toast.error(e?.response?.data?.detail || "Could not change that.") },
    );

  const confirmDelete = async () => {
    try {
      await remove.mutateAsync(deleting.id);
      toast.success(`${deleting.name} deleted.`);
      setDeleting(null);
    } catch (e) {
      // 409: bookings use it. The server's message says to turn it off instead.
      toast.error(e?.response?.data?.detail || "Could not delete that.");
      setDeleting(null);
    }
  };

  const pending = create.isPending || update.isPending || upload.isPending;

  return (
    <>
      <PageIntro
        title="Vehicles"
        subtitle="The models this brand services. Advisors pick one when booking, and its photo becomes the group's picture across the dashboard."
        actionLabel="Add vehicle"
        onAction={() => openAdd()}
      />

      <NeedsAdding tenantId={tenantId} onAdd={openAdd} />

      {rows.length > 0 && (
        <Toolbar count={`${shown.length} of ${rows.length}`}>
          <SearchBox value={query} onChange={setQuery} placeholder="Search vehicles" data-testid="vehicle-search" />
        </Toolbar>
      )}

      {rows.length === 0 ? (
        <EmptyState title="No vehicles yet" body="Add the first model and upload its photo." actionLabel="Add vehicle" onAction={() => openAdd()} />
      ) : shown.length === 0 ? (
        <EmptyState title="Nothing matches that search" body="Clear it, or add the vehicle that should be here." />
      ) : (
        <Table columns={["Photo", "Vehicle", "Body type", "Available", ""]}>
          {shown.map((row) => (
            <tr key={row.id} data-testid="vehicle-row">
              <Cell><Thumb url={row.thumb_url} name={row.name} /></Cell>
              <Cell><div className="text-[14px] font-medium text-[#111827]">{row.name}</div></Cell>
              <Cell>{bodyTypeLabel(row.body_type)}</Cell>
              <Cell><Toggle checked={row.active} onChange={() => toggleActive(row)} label={`${row.name} available`} /></Cell>
              <Cell className="whitespace-nowrap text-right">
                <button
                  type="button"
                  onClick={() => openEdit(row)}
                  aria-label={`Edit ${row.name}`}
                  className="mr-1 inline-flex h-8 w-8 items-center justify-center rounded-lg text-[#9CA3AF] hover:bg-[#F3F4F6] hover:text-[#111827]"
                >
                  <Pencil size={15} />
                </button>
                <button
                  type="button"
                  onClick={() => setDeleting(row)}
                  aria-label={`Delete ${row.name}`}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-[#9CA3AF] hover:bg-[#FEF2F2] hover:text-[#DC2626]"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 7h16M9 7V5h6v2M6 7l1 13h10l1-13" />
                  </svg>
                </button>
              </Cell>
            </tr>
          ))}
        </Table>
      )}

      <FormDialog
        open={open}
        onOpenChange={setOpen}
        title={editing ? `Edit ${editing.name}` : "Add vehicle"}
        onSubmit={submit}
        submitLabel={editing ? "Save changes" : "Add vehicle"}
        pending={pending}
      >
        <div>
          <FieldLabel>Vehicle name</FieldLabel>
          <input
            type="text"
            className={inputClass}
            value={form.name}
            placeholder="Grand Vitara"
            autoFocus
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <FieldError>{errors.name}</FieldError>
        </div>
        <div>
          <FieldLabel>Body type</FieldLabel>
          <Select value={form.body_type} onChange={(e) => setForm({ ...form, body_type: e.target.value })}>
            {BODY_TYPES.map((b) => (
              <option key={b.value} value={b.value}>{b.label}</option>
            ))}
          </Select>
        </div>
        <div>
          <FieldLabel>Photo</FieldLabel>
          <div className="flex items-center gap-3">
            <span className="flex h-[72px] w-[128px] items-center justify-center overflow-hidden rounded-xl bg-[#F9FAFB] shadow-[0_0_0_1px_#E5E7EB]">
              {preview ? <img src={preview} alt="" className="h-full w-full object-cover" /> : <ImagePlus size={22} className="text-[#9CA3AF]" />}
            </span>
            <div>
              <input
                ref={fileRef}
                type="file"
                accept=".jpg,.jpeg,.png,.webp"
                className="hidden"
                data-testid="vehicle-photo-input"
                onChange={(e) => {
                  setFile(e.target.files?.[0] || null);
                  e.target.value = "";
                }}
              />
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="inline-flex h-9 items-center gap-2 rounded-lg border border-[#D1D5DB] bg-white px-3 text-[13px] font-medium text-[#111827] hover:border-[#9CA3AF]"
              >
                {upload.isPending && <Loader2 className="animate-spin" size={14} />}
                {preview ? "Replace photo" : "Choose photo"}
              </button>
              <p className="mt-1.5 text-[12px] text-[#6B7280]">JPG, PNG or WEBP · up to 5 MB. Cropped automatically.</p>
            </div>
          </div>
        </div>
      </FormDialog>

      <ConfirmDelete
        open={!!deleting}
        onOpenChange={(v) => !v && setDeleting(null)}
        name={deleting?.name || ""}
        body="Bookings that already use it will block this. Turn it off instead if you only want to hide it."
        onConfirm={confirmDelete}
        pending={remove.isPending}
      />
    </>
  );
}
